import { Component } from '@theme/component';
import { prefersReducedMotion, preloadImage } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Product media lightbox.
 *
 * The native Horizon zoom dialog is kept as the underlying accessible dialog,
 * while navigation is changed to a single-media editorial viewer. This avoids
 * duplicating the product gallery and keeps variant/media IDs in sync.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #activeIndex = 0;
  #touchStartX = null;
  #touchStartY = null;

  connectedCallback() {
    super.connectedCallback();

    const { dialog, media } = this.refs;

    this.#installEditorialControls();

    // Keep initial focus on the dialog itself rather than a control button.
    // This prevents the browser from showing an unwanted focus ring around the
    // center close control when the viewer first opens, while keyboard users
    // can still Tab into the visible controls normally.
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');

    // The previous viewer closed when the large image itself was clicked.
    // The editorial viewer keeps the image inert and reserves closing for the
    // center close control, Escape, or the empty backdrop.
    media.forEach((item) => {
      item.removeAttribute('on:click');
      item.querySelectorAll('img').forEach((image) => {
        image.draggable = false;
      });
    });

    // Prevent Chrome/WebKit from turning a small mouse drag or repeated click
    // into the native blue image-selection / drag highlight.
    dialog.style.userSelect = 'none';
    dialog.style.webkitUserSelect = 'none';

    this.#setActiveIndex(0, { emit: false, preload: false });

    dialog.addEventListener('cancel', this.handleNativeCancel);
    dialog.addEventListener('click', this.handleBackdropClick);
    dialog.addEventListener('dragstart', this.handleNativeDragStart);
    dialog.addEventListener('selectstart', this.handleNativeSelectStart);
    dialog.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    dialog.addEventListener('touchend', this.handleTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    const { dialog } = this.refs;

    dialog.removeEventListener('cancel', this.handleNativeCancel);
    dialog.removeEventListener('click', this.handleBackdropClick);
    dialog.removeEventListener('dragstart', this.handleNativeDragStart);
    dialog.removeEventListener('selectstart', this.handleNativeSelectStart);
    dialog.removeEventListener('touchstart', this.handleTouchStart);
    dialog.removeEventListener('touchend', this.handleTouchEnd);

    super.disconnectedCallback();
  }

  /**
   * Opens the viewer at the media that was actually clicked. Matching by media
   * ID keeps the lightbox correct even when KERVELAN reorders the visible PDP
   * gallery by product-image Alt Text.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  open(index, event) {
    event?.preventDefault();

    const { dialog } = this.refs;
    const resolvedIndex = this.#resolveIndexFromSource(index, event);

    this.#setActiveIndex(resolvedIndex, { emit: true, preload: true });
    dialog.classList.remove('kervelan-zoom-dialog--ready', 'kervelan-zoom-dialog--closing');

    if (!dialog.open) dialog.showModal();

    // showModal() may autofocus the first focusable control. Move focus to the
    // dialog immediately so no control appears selected on initial open.
    dialog.focus({ preventScroll: true });

    requestAnimationFrame(() => {
      dialog.classList.add('kervelan-zoom-dialog--ready');
    });
  }

  /**
   * Loads the high-resolution source for the active media.
   *
   * @param {HTMLElement} mediaContainer
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.draggable = false;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
    return true;
  }

  /** Move to the previous image and wrap first -> last. */
  previous(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const count = this.refs.media.length;
    if (!count) return;

    this.#setActiveIndex((this.#activeIndex - 1 + count) % count);
  }

  /** Move to the next image and wrap last -> first. */
  next(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const count = this.refs.media.length;
    if (!count) return;

    this.#setActiveIndex((this.#activeIndex + 1) % count);
  }

  /**
   * Closes with a restrained fade instead of the old zoom-out transition.
   */
  async close() {
    const { dialog } = this.refs;
    if (!dialog.open || dialog.classList.contains('kervelan-zoom-dialog--closing')) return;

    dialog.classList.add('kervelan-zoom-dialog--closing');
    dialog.classList.remove('kervelan-zoom-dialog--ready');

    if (!prefersReducedMotion()) {
      await new Promise((resolve) => window.setTimeout(resolve, 180));
    }

    this.closeDialog();
    dialog.classList.remove('kervelan-zoom-dialog--closing');
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (dialog.open) dialog.close();
    dialog.classList.remove('kervelan-zoom-dialog--ready');
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Keyboard navigation: Escape closes; left/right arrows change media.
   *
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous(event);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next(event);
    }
  }

  /**
   * Kept for compatibility with Horizon's hidden thumbnail markup.
   *
   * @param {number} index
   */
  handleThumbnailClick(index) {
    this.#setActiveIndex(index);
  }

  /**
   * @param {number} index
   */
  handleThumbnailPointerEnter(index) {
    const target = this.refs.media[index];
    if (target) this.loadHighResolutionImage(target);
  }

  /**
   * Compatibility alias used by existing component actions.
   *
   * @param {number} index
   */
  selectThumbnail(index) {
    this.#setActiveIndex(index);
  }

  handleNativeCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  handleNativeDragStart = (event) => {
    event.preventDefault();
  };

  handleNativeSelectStart = (event) => {
    event.preventDefault();
  };

  handleBackdropClick = (event) => {
    const target = event.target;
    const gallery = this.refs.dialog.querySelector('.dialog-zoomed-gallery');

    if (target === this.refs.dialog || target === gallery) this.close();
  };

  handleTouchStart = (event) => {
    if (event.touches.length !== 1) return;
    this.#touchStartX = event.touches[0].clientX;
    this.#touchStartY = event.touches[0].clientY;
  };

  handleTouchEnd = (event) => {
    if (this.#touchStartX === null || this.#touchStartY === null || event.changedTouches.length !== 1) return;

    const deltaX = event.changedTouches[0].clientX - this.#touchStartX;
    const deltaY = event.changedTouches[0].clientY - this.#touchStartY;

    this.#touchStartX = null;
    this.#touchStartY = null;

    if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    if (deltaX < 0) this.next();
    else this.previous();
  };

  #installEditorialControls() {
    const { dialog } = this.refs;
    if (dialog.querySelector('.kervelan-zoom-dialog__controls')) return;

    const controls = document.createElement('div');
    controls.className = 'kervelan-zoom-dialog__controls';
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', 'Image viewer controls');

    const previousButton = this.#createControlButton(
      'kervelan-zoom-dialog__previous',
      'Previous image',
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 6.5 9 12l5.5 5.5"/></svg>'
    );
    previousButton.addEventListener('click', (event) => this.previous(event));

    const closeButton = this.#createControlButton(
      'kervelan-zoom-dialog__close',
      'Close image viewer',
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17"/></svg>'
    );
    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });

    const nextButton = this.#createControlButton(
      'kervelan-zoom-dialog__next',
      'Next image',
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.5 6.5 15 12l-5.5 5.5"/></svg>'
    );
    nextButton.addEventListener('click', (event) => this.next(event));

    controls.append(previousButton, closeButton, nextButton);
    dialog.appendChild(controls);
    this.#updateControls();
  }

  #createControlButton(className, label, icon) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `kervelan-zoom-dialog__control ${className}`;
    button.setAttribute('aria-label', label);
    button.innerHTML = icon;
    return button;
  }

  #updateControls() {
    const { dialog, media } = this.refs;
    const previousButton = dialog.querySelector('.kervelan-zoom-dialog__previous');
    const nextButton = dialog.querySelector('.kervelan-zoom-dialog__next');
    const navigationAvailable = media.length > 1;

    if (previousButton instanceof HTMLButtonElement) {
      previousButton.disabled = !navigationAvailable;
    }

    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled = !navigationAvailable;
    }
  }

  #setActiveIndex(index, options = { emit: true, preload: true }) {
    const { media, thumbnails } = this.refs;
    if (!media.length) return;

    const parsedIndex = Number(index);
    const safeIndex = Number.isFinite(parsedIndex)
      ? Math.max(0, Math.min(media.length - 1, parsedIndex))
      : 0;

    this.#activeIndex = safeIndex;

    media.forEach((item, itemIndex) => {
      const active = itemIndex === safeIndex;
      item.hidden = !active;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, buttonIndex) => {
      button.setAttribute('aria-selected', buttonIndex === safeIndex ? 'true' : 'false');
    });

    const activeMedia = media[safeIndex];
    if (options.preload !== false && activeMedia) this.loadHighResolutionImage(activeMedia);

    this.#updateControls();

    if (options.emit !== false) {
      this.dispatchEvent(new ZoomMediaSelectedEvent(safeIndex));
    }
  }

  #resolveIndexFromSource(index, event) {
    const { media } = this.refs;
    const target = event?.target;

    if (target instanceof Element) {
      const source = target.closest('slideshow-slide, li.product-media-container');
      const sourceMediaId = source
        ?.querySelector('.product-media[data-media-id]')
        ?.getAttribute('data-media-id');

      if (sourceMediaId) {
        const matchingIndex = media.findIndex(
          (item) =>
            item.querySelector('.product-media[data-media-id]')?.getAttribute('data-media-id') === sourceMediaId
        );

        if (matchingIndex >= 0) return matchingIndex;
      }
    }

    const parsedIndex = Number(index);
    return Number.isFinite(parsedIndex) ? parsedIndex : 0;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
